import React, { useRef, useEffect, useMemo } from 'react';
import { usePdfThumbnails } from '../../../hooks/usePdfThumbnails';
import { pageSetToRanges } from '../../../utils/pageRangeConvert';
import { isTouchDevice, isHeavyPdf } from '../../../utils/deviceDetection';

interface PageRangeSelectorProps {
  pdfBlob: Blob | null;
  selectedPages: Set<number>;
  onChange: (next: Set<number>) => void;
  /** issue #31: 新しい PDF (doc 世代変化) の pageCount 確定時、未選択なら全ページで初期化する
   *  コールバック。onChange と違い「初期化」であり settingsChanged 等の副作用を持たない用途。 */
  onInit?: (next: Set<number>) => void;
}

function rangesToLabel(ranges: number[][]): string {
  return ranges.map(([s, e]) => (s === e ? `${s}` : `${s}-${e}`)).join(',');
}

const PageRangeSelector: React.FC<PageRangeSelectorProps> = ({ pdfBlob, selectedPages, onChange, onInit }) => {
  const { pageCount, thumbnails, ensureThumbnail, generation, error } = usePdfThumbnails(pdfBlob);

  const summary = useMemo(() => rangesToLabel(pageSetToRanges(selectedPages)), [selectedPages]);

  // issue #31: 新しい PDF (generation 変化) の pageCount 確定時、未選択なら全ページを初期選択にする
  // (初期=全投入と一致)。ユーザーが先に手動選択していた場合 (size>0) は尊重して上書きしない。
  const initedGenRef = useRef(0);
  useEffect(() => {
    if (pageCount > 0 && generation !== initedGenRef.current) {
      initedGenRef.current = generation;
      if (selectedPages.size === 0 && onInit) {
        onInit(new Set(Array.from({ length: pageCount }, (_, i) => i + 1)));
      }
    }
  }, [generation, pageCount, selectedPages, onInit]);

  if (!pdfBlob) return null;
  // issue #60: 読み込み失敗を無音で握りつぶさず表面化する。数字入力へのフォールバックを促す。
  if (error && pageCount === 0) {
    return (
      <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
        ⚠️ PDF のサムネイルを読み込めませんでした。 ページ指定は数字入力欄をお使いください。
      </div>
    );
  }
  if (pageCount === 0) return null;

  // issue #179: タッチ端末 / 重いPDF はサムネ描画でメモリ枯渇クラッシュしうるため (#176 の保険)、
  // ThumbCell を mount せず番号入力フォールバックへ自動で切り替える。ThumbCell を出さないことで
  // IntersectionObserver → ensureThumbnail → render() が起動せず、メモリピークそのものを作らない。
  // onInit の全ページ初期化 useEffect はこの return より前なので、フォールバックでも初期化は維持される。
  if (isTouchDevice() || isHeavyPdf(pdfBlob.size, pageCount)) {
    return (
      <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
        <p className="font-bold text-gray-700">端末の負荷軽減のため、サムネイル表示を省略しています。</p>
        <p>
          ページは詳細設定の「処理対象ページ範囲」欄に番号でご指定ください（空欄で全ページ処理）。
        </p>
      </div>
    );
  }

  const toggle = (n: number) => {
    const next = new Set(selectedPages);
    if (next.has(n)) next.delete(n);
    else next.add(n);
    onChange(next);
  };
  const selectAll = () => onChange(new Set(Array.from({ length: pageCount }, (_, i) => i + 1)));
  const clearAll = () => onChange(new Set());

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {selectedPages.size} / {pageCount} ページ選択中{summary ? ` (${summary})` : ''}
        </span>
        <span className="flex gap-2">
          <button type="button" onClick={selectAll} className="underline">
            全選択
          </button>
          <button type="button" onClick={clearAll} className="underline">
            全解除
          </button>
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
          <ThumbCell
            key={n}
            pageNumber={n}
            generation={generation}
            url={thumbnails.get(n) ?? null}
            selected={selectedPages.has(n)}
            onEnsure={ensureThumbnail}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  );
};

interface ThumbCellProps {
  pageNumber: number;
  // issue #60: doc 世代。pdfBlob 差し替え (2 回目以降) で値が変わると IO を張り直し、
  // 新しい doc のサムネイル描画を再トリガする (2 回目白紙バグ対策)。
  generation: number;
  url: string | null;
  selected: boolean;
  onEnsure: (n: number) => void;
  onToggle: (n: number) => void;
}

const ThumbCell: React.FC<ThumbCellProps> = ({ pageNumber, generation, url, selected, onEnsure, onToggle }) => {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onEnsure(pageNumber);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [pageNumber, generation, onEnsure]);

  return (
    <button
      ref={ref}
      type="button"
      aria-label={`ページ ${pageNumber}`}
      aria-pressed={selected}
      onClick={() => onToggle(pageNumber)}
      className={`relative min-h-[44px] rounded-lg border-2 overflow-hidden transition-all ${
        selected ? 'border-brand-purple ring-2 ring-purple-200' : 'border-gray-200 opacity-60'
      }`}
    >
      {url ? (
        <img src={url} alt="" className="w-full h-auto block" />
      ) : (
        <div className="w-full aspect-[210/297] bg-gray-100 animate-pulse" />
      )}
      <span className="absolute bottom-0.5 right-0.5 px-1 text-[10px] font-bold bg-white/80 rounded">
        {pageNumber}
      </span>
      {selected && (
        <span className="absolute top-0.5 left-0.5 w-4 h-4 flex items-center justify-center bg-brand-purple text-white text-[10px] rounded-full">
          ✓
        </span>
      )}
    </button>
  );
};

export default PageRangeSelector;
